/**
 * ScenarioModelingPage — 13-Week Cash Flow Scenario Comparison Tool.
 *
 * Allows users to define Base / Best / Worst case assumptions, compute each
 * against the underlying weekly forecast model, and compare ending-cash
 * trajectories on a single Recharts line chart with threshold reference
 * lines and a summary metrics table.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Area,
  ResponsiveContainer,
  type TooltipProps,
} from 'recharts';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useFacilityConfig, useComputeScenario } from './api';
import { formatCurrency, formatWeekLabel } from './utils';
import { cn } from '../../shared/lib/cn';
import type {
  ScenarioParams,
  ScenarioSummaryMetrics,
  CapexTiming,
  ScenarioComputeResult,
} from './types';
import { DEFAULT_SCENARIO_PARAMS } from './types';

const DEBOUNCE_MS = 250;

// ── Preset defaults ─────────────────────────────────────────────────

const BEST_CASE_DEFAULTS: ScenarioParams = {
  ar_collection_delay_days: -7,
  ar_collection_rate: 98,
  ap_payment_accel_days: 0,
  revenue_pipeline_multiplier: 120,
  payroll_adjustment_pct: 100,
  capex_timing: 'as_planned',
};

const WORST_CASE_DEFAULTS: ScenarioParams = {
  ar_collection_delay_days: 14,
  ar_collection_rate: 70,
  ap_payment_accel_days: -7,
  revenue_pipeline_multiplier: 60,
  payroll_adjustment_pct: 105,
  capex_timing: 'as_planned',
};

// ── Slider config ───────────────────────────────────────────────────

interface SliderDef {
  key: keyof Omit<ScenarioParams, 'capex_timing'>;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
}

const SLIDER_DEFS: SliderDef[] = [
  { key: 'ar_collection_delay_days', label: 'AR Collection Delay', min: -14, max: 21, step: 1, unit: ' days' },
  { key: 'ar_collection_rate', label: 'AR Collection Rate', min: 50, max: 100, step: 1, unit: '%' },
  { key: 'ap_payment_accel_days', label: 'AP Payment Acceleration', min: -14, max: 14, step: 1, unit: ' days' },
  { key: 'revenue_pipeline_multiplier', label: 'Revenue Pipeline', min: 0, max: 150, step: 5, unit: '%' },
  { key: 'payroll_adjustment_pct', label: 'Payroll Adjustment', min: 90, max: 110, step: 1, unit: '%' },
];

const CAPEX_OPTIONS: { value: CapexTiming; label: string }[] = [
  { value: 'as_planned', label: 'As Planned' },
  { value: 'defer_all', label: 'Defer' },
  { value: 'accelerate', label: 'Accelerate' },
];

// ── Chart colors ────────────────────────────────────────────────────

const SERIES_COLORS = {
  base: '#6366f1',   // indigo-500
  best: '#10b981',   // emerald-500
  worst: '#ef4444',  // red-500
} as const;

type ScenarioKey = 'base' | 'best' | 'worst';

const SCENARIO_LABELS: Record<ScenarioKey, string> = {
  base: 'Base Case',
  best: 'Best Case',
  worst: 'Worst Case',
};

// ── Slider Component ────────────────────────────────────────────────

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
  accentColor?: string;
}

function Slider({ label, value, min, max, step, unit, onChange, accentColor }: SliderProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
        <span className="text-xs font-mono tabular-nums text-charcoal dark:text-gray-200">
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-gray-200 dark:bg-dark-border rounded-lg appearance-none cursor-pointer"
        style={accentColor ? { accentColor } : undefined}
      />
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

// ── Custom Tooltip ──────────────────────────────────────────────────

interface ChartPoint {
  week: string;
  weekLabel: string;
  base?: number;
  best?: number;
  worst?: number;
}

function ScenarioTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as ChartPoint | undefined;
  const baseVal = point?.base;

  return (
    <div className="bg-gray-900 text-white rounded-lg shadow-xl px-4 py-3 text-xs space-y-1.5 min-w-[200px]">
      <p className="font-semibold text-gray-300 border-b border-gray-700 pb-1.5">
        {point?.weekLabel ?? label}
      </p>
      {payload.map((entry) => {
        const val = entry.value ?? 0;
        const key = entry.dataKey as ScenarioKey;
        const delta = baseVal != null && key !== 'base' ? val - baseVal : null;
        return (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ backgroundColor: entry.color }}
              />
              <span>{SCENARIO_LABELS[key] ?? entry.name}</span>
            </div>
            <div className="text-right">
              <span className="font-mono">{formatCurrency(val, true)}</span>
              {delta != null && (
                <span className={cn(
                  'ml-1.5 font-mono',
                  delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-gray-500',
                )}>
                  ({delta > 0 ? '+' : ''}{formatCurrency(delta, true)})
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Scenario Input Panel ────────────────────────────────────────────

interface ScenarioInputPanelProps {
  activeScenario: ScenarioKey;
  params: Record<ScenarioKey, ScenarioParams>;
  onParamChange: (scenario: ScenarioKey, key: keyof ScenarioParams, value: number | string) => void;
  onReset: (scenario: ScenarioKey) => void;
  onSelectScenario: (scenario: ScenarioKey) => void;
  computing: boolean;
}

function ScenarioInputPanel({
  activeScenario,
  params,
  onParamChange,
  onReset,
  onSelectScenario,
  computing,
}: ScenarioInputPanelProps) {
  const currentParams = params[activeScenario];
  const color = SERIES_COLORS[activeScenario];

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        {/* Scenario tabs */}
        <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-dark-surface-2 rounded-lg">
          {(['base', 'best', 'worst'] as ScenarioKey[]).map(key => (
            <button
              key={key}
              onClick={() => onSelectScenario(key)}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-all',
                activeScenario === key
                  ? 'bg-white dark:bg-dark-surface shadow-sm text-charcoal dark:text-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
              )}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: SERIES_COLORS[key] }}
              />
              {SCENARIO_LABELS[key]}
            </button>
          ))}
        </div>

        {/* Sliders */}
        <div className="space-y-4">
          {SLIDER_DEFS.map(def => (
            <Slider
              key={def.key}
              label={def.label}
              value={currentParams[def.key] as number}
              min={def.min}
              max={def.max}
              step={def.step}
              unit={def.unit}
              onChange={v => onParamChange(activeScenario, def.key, v)}
              accentColor={color}
            />
          ))}

          <div className="pt-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">CapEx Timing</label>
            <select
              value={currentParams.capex_timing}
              onChange={e => onParamChange(activeScenario, 'capex_timing', e.target.value)}
              className="mt-1 w-full text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface"
            >
              {CAPEX_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Reset */}
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={() => onReset(activeScenario)}
            className="text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
          >
            Reset {SCENARIO_LABELS[activeScenario]}
          </button>
          {computing && (
            <span className="text-[10px] text-gray-400 animate-pulse">Computing...</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Summary Metrics Table ───────────────────────────────────────────

interface MetricsTableProps {
  metrics: ScenarioSummaryMetrics[];
  threshold: number;
}

function MetricsTable({ metrics, threshold }: MetricsTableProps) {
  if (!metrics.length) return null;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
            Scenario Summary
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border">
                <th className="text-left py-2 px-3 font-medium text-gray-500">Scenario</th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">Min Cash Week</th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">Min Cash Amount</th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">Week 13 Ending</th>
                <th className="text-center py-2 px-3 font-medium text-gray-500">Threshold Status</th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">Δ vs Base</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
              {metrics.map(m => (
                <tr key={m.name} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                  <td className="py-2 px-3 font-medium text-charcoal dark:text-gray-200">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5"
                      style={{
                        backgroundColor:
                          m.name === 'Base Case' ? SERIES_COLORS.base
                          : m.name === 'Best Case' ? SERIES_COLORS.best
                          : SERIES_COLORS.worst,
                      }}
                    />
                    {m.name}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">W{m.min_cash_week}</td>
                  <td className={cn(
                    'py-2 px-3 text-right tabular-nums font-medium',
                    m.min_cash_amount < threshold ? 'text-red-600' : 'text-charcoal dark:text-gray-200',
                  )}>
                    {formatCurrency(m.min_cash_amount, true)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold text-charcoal dark:text-gray-200">
                    {formatCurrency(m.ending_cash_week13, true)}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={cn(
                      'inline-block px-2 py-0.5 rounded text-[10px] font-medium',
                      m.breach_status === 'clear'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : m.breach_status === 'at_risk'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                    )}>
                      {m.breach_status === 'clear' ? 'Clear' : m.breach_status === 'at_risk' ? 'At Risk' : `Breach W${m.breach_week}`}
                    </span>
                  </td>
                  <td className={cn(
                    'py-2 px-3 text-right tabular-nums',
                    m.delta_vs_base > 0 ? 'text-emerald-600' : m.delta_vs_base < 0 ? 'text-red-600' : 'text-gray-400',
                  )}>
                    {m.name === 'Base Case' ? '—' : `${m.delta_vs_base > 0 ? '+' : ''}${formatCurrency(m.delta_vs_base, true)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ───────────────────────────────────────────────────────

export default function ScenarioModelingPage() {
  const { data: summary } = useForecastSummary();
  const { data: facility } = useFacilityConfig();
  const computeScenario = useComputeScenario();

  const forecastId = summary?.forecast_id;
  const baseWeeks = summary?.weeks ?? [];

  const threshold = facility?.minimum_cash_covenant ?? 12_000_000;
  const criticalThreshold = threshold * 0.75;

  // Scenario parameter state
  const [allParams, setAllParams] = useState<Record<ScenarioKey, ScenarioParams>>({
    base: { ...DEFAULT_SCENARIO_PARAMS },
    best: { ...BEST_CASE_DEFAULTS },
    worst: { ...WORST_CASE_DEFAULTS },
  });

  // Computed results per scenario
  const [results, setResults] = useState<Record<ScenarioKey, ScenarioComputeResult | null>>({
    base: null,
    best: null,
    worst: null,
  });

  const [activeTab, setActiveTab] = useState<ScenarioKey>('base');
  const [computingSet, setComputingSet] = useState<Set<ScenarioKey>>(new Set());
  const timerRefs = useRef<Record<ScenarioKey, ReturnType<typeof setTimeout> | null>>({
    base: null,
    best: null,
    worst: null,
  });

  // Compute a single scenario
  const computeOne = useCallback(
    (key: ScenarioKey, params: ScenarioParams) => {
      if (!forecastId) return;
      setComputingSet(prev => new Set(prev).add(key));
      computeScenario.mutate(
        { forecast_id: forecastId, ...params },
        {
          onSuccess: (data) => {
            setResults(prev => ({ ...prev, [key]: data }));
            setComputingSet(prev => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            });
          },
          onError: () => {
            setComputingSet(prev => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            });
          },
        },
      );
    },
    [forecastId, computeScenario],
  );

  const debouncedCompute = useCallback(
    (key: ScenarioKey, params: ScenarioParams) => {
      if (timerRefs.current[key]) clearTimeout(timerRefs.current[key]!);
      timerRefs.current[key] = setTimeout(() => computeOne(key, params), DEBOUNCE_MS);
    },
    [computeOne],
  );

  // Kick off initial computations when forecast loads
  useEffect(() => {
    if (!forecastId) return;
    (['base', 'best', 'worst'] as ScenarioKey[]).forEach(key => {
      computeOne(key, allParams[key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastId]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      Object.values(timerRefs.current).forEach(t => { if (t) clearTimeout(t); });
    };
  }, []);

  // Handlers
  const handleParamChange = useCallback(
    (scenario: ScenarioKey, key: keyof ScenarioParams, value: number | string) => {
      setAllParams(prev => {
        const next = { ...prev, [scenario]: { ...prev[scenario], [key]: value } };
        debouncedCompute(scenario, next[scenario]);
        return next;
      });
    },
    [debouncedCompute],
  );

  const handleReset = useCallback(
    (scenario: ScenarioKey) => {
      const defaults: Record<ScenarioKey, ScenarioParams> = {
        base: { ...DEFAULT_SCENARIO_PARAMS },
        best: { ...BEST_CASE_DEFAULTS },
        worst: { ...WORST_CASE_DEFAULTS },
      };
      setAllParams(prev => ({ ...prev, [scenario]: defaults[scenario] }));
      computeOne(scenario, defaults[scenario]);
    },
    [computeOne],
  );

  // Build chart data
  const chartData: ChartPoint[] = useMemo(() => {
    const weekCount = 13;
    const points: ChartPoint[] = [];

    for (let i = 0; i < weekCount; i++) {
      const baseSrc = results.base?.weeks[i] ?? baseWeeks[i];
      const bestSrc = results.best?.weeks[i];
      const worstSrc = results.worst?.weeks[i];
      const weekLabel = baseSrc
        ? `W${baseSrc.week_number} (${formatWeekLabel(baseSrc.week_start_date)})`
        : `W${i + 1}`;

      points.push({
        week: `W${i + 1}`,
        weekLabel,
        base: baseSrc?.ending_cash,
        best: bestSrc?.ending_cash,
        worst: worstSrc?.ending_cash,
      });
    }
    return points;
  }, [results, baseWeeks]);

  // Build summary metrics
  const summaryMetrics: ScenarioSummaryMetrics[] = useMemo(() => {
    const makeMetrics = (
      key: ScenarioKey,
      result: ScenarioComputeResult | null,
    ): ScenarioSummaryMetrics | null => {
      if (!result) return null;
      const baseEnding = results.base?.ending_cash_week13 ?? 0;
      const cushion = result.min_cash_amount - threshold;
      return {
        name: SCENARIO_LABELS[key],
        min_cash_week: result.min_cash_week,
        min_cash_amount: result.min_cash_amount,
        ending_cash_week13: result.ending_cash_week13,
        breach_status: result.covenant_breach_week
          ? 'breach'
          : cushion < threshold * 0.1
            ? 'at_risk'
            : 'clear',
        breach_week: result.covenant_breach_week,
        delta_vs_base: key === 'base' ? 0 : result.ending_cash_week13 - baseEnding,
      };
    };

    return (['base', 'best', 'worst'] as ScenarioKey[])
      .map(key => makeMetrics(key, results[key]))
      .filter((m): m is ScenarioSummaryMetrics => m !== null);
  }, [results, threshold]);

  // Y-axis domain
  const yDomain = useMemo(() => {
    const allValues = chartData.flatMap(p => [p.base, p.best, p.worst].filter((v): v is number => v != null));
    allValues.push(threshold, criticalThreshold);
    if (!allValues.length) return [0, 100_000_000];
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 5_000_000;
    return [Math.floor((min - padding) / 1_000_000) * 1_000_000, Math.ceil((max + padding) / 1_000_000) * 1_000_000];
  }, [chartData, threshold, criticalThreshold]);

  const isComputing = computingSet.size > 0;
  const hasAnyData = chartData.some(p => p.base != null || p.best != null || p.worst != null);

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        Loading forecast data...
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-2">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Scenario Input Panel */}
        <div className="lg:col-span-1 space-y-4">
          <ScenarioInputPanel
            activeScenario={activeTab}
            params={allParams}
            onParamChange={handleParamChange}
            onReset={handleReset}
            onSelectScenario={setActiveTab}
            computing={isComputing}
          />

          {/* Threshold info */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Liquidity Thresholds
              </h4>
              <div className="flex items-center gap-2">
                <span className="w-4 h-0 border-t-2 border-dashed border-amber-500" />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Minimum: {formatCurrency(threshold, true)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-0 border-t-2 border-dashed border-red-500" />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Critical: {formatCurrency(criticalThreshold, true)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Chart */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
                  13-Week Ending Cash — Scenario Comparison
                </h3>
                {isComputing && (
                  <span className="text-[10px] text-gray-400 animate-pulse">Updating...</span>
                )}
              </div>

              <div className="p-4" style={{ height: 420 }}>
                {hasAnyData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <defs>
                        <linearGradient id="scenarioBand" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={SERIES_COLORS.best} stopOpacity={0.08} />
                          <stop offset="100%" stopColor={SERIES_COLORS.worst} stopOpacity={0.08} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-gray-200, #e5e7eb)"
                        className="dark:opacity-20"
                      />
                      <XAxis
                        dataKey="week"
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 11 }}
                        tickFormatter={(v: number) => formatCurrency(v, true)}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        domain={yDomain}
                        width={60}
                      />
                      <Tooltip content={<ScenarioTooltip />} />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        formatter={(value: string) => (
                          <span className="text-xs text-gray-600 dark:text-gray-400">{value}</span>
                        )}
                      />

                      {/* Scenario range shading (Best to Worst) */}
                      {results.best && results.worst && (
                        <>
                          <Area
                            dataKey="best"
                            stroke="none"
                            fill="url(#scenarioBand)"
                            fillOpacity={1}
                            name="_bandTop"
                            legendType="none"
                            tooltipType="none"
                          />
                        </>
                      )}

                      {/* Threshold lines */}
                      <ReferenceLine
                        y={threshold}
                        stroke="#f59e0b"
                        strokeDasharray="6 4"
                        strokeWidth={1.5}
                        label={{
                          value: 'Min Threshold',
                          position: 'right',
                          fill: '#f59e0b',
                          fontSize: 10,
                        }}
                      />
                      <ReferenceLine
                        y={criticalThreshold}
                        stroke="#ef4444"
                        strokeDasharray="4 4"
                        strokeWidth={1}
                        label={{
                          value: 'Critical',
                          position: 'right',
                          fill: '#ef4444',
                          fontSize: 10,
                        }}
                      />

                      {/* Scenario lines */}
                      <Line
                        type="monotone"
                        dataKey="base"
                        name="Base Case"
                        stroke={SERIES_COLORS.base}
                        strokeWidth={2.5}
                        dot={{ r: 3.5, fill: SERIES_COLORS.base, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="best"
                        name="Best Case"
                        stroke={SERIES_COLORS.best}
                        strokeWidth={2}
                        strokeDasharray="0"
                        dot={{ r: 3, fill: SERIES_COLORS.best, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="worst"
                        name="Worst Case"
                        stroke={SERIES_COLORS.worst}
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={{ r: 3, fill: SERIES_COLORS.worst, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    <div className="text-center space-y-2">
                      <div className="animate-pulse">Computing scenarios...</div>
                      <p className="text-[11px]">Calculating Base, Best, and Worst case projections</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Summary Metrics */}
      <MetricsTable metrics={summaryMetrics} threshold={threshold} />
    </div>
  );
}
